import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Event, NavigationEnd, Router, RouterEvent, Scroll } from '@angular/router';
import { map, filter, Subscription } from 'rxjs';
import { Validator } from '@libs/utilities/validator';
import { Util } from '@libs/utilities/util';
import { EnumLanguage } from '@libs/constants';
import { AddonLanguageTranslationService } from '@domains/frontend/page-addons';

import { EnumLegalInjectionToken, EnumLegalPageRoute, ILegalPagesConfig, LegalPagesConfigSchema } from './legal-pages.interface';
import { ILegalCookieUsageContent } from './cookies-usage/cookies-usage.component';
import { LegalContentGenerator } from '../../services/legal-pages/legal-content-generator/legal-content-generator';
import { LEGAL_AFFILIATE_TERMS_TEMPLATE_DE } from '../../constants/legal-texts-de/affiliate-terms';
import { LEGAL_CANCELLATION_POLICY_TEMPLATE_DE } from '../../constants/legal-texts-de/cancellation-policy';
import { LEGAL_COOKIES_USAGE_INTRO_DE, LEGAL_COOKIES_USAGE_CODE_DE } from '../../constants/legal-texts-de/cookies-usage';
import { LEGAL_IMPRINT_TEMPLATE_DE } from '../../constants/legal-texts-de/imprint';
import { LEGAL_PRIVACY_POLICY_TEMPLATE_DE } from '../../constants/legal-texts-de/privacy-policy';
import { LEGAL_TERMS_OF_USE_TEMPLATE_DE } from '../../constants/legal-texts-de/terms-of-use';
import { LEGAL_AFFILIATE_TERMS_TEMPLATE_EN } from '../../constants/legal-texts-en/affiliate-terms';
import { LEGAL_CANCELLATION_POLICY_TEMPLATE_EN } from '../../constants/legal-texts-en/cancellation-policy';
import { LEGAL_COOKIES_USAGE_INTRO_EN, LEGAL_COOKIES_USAGE_CODE_EN } from '../../constants/legal-texts-en/cookies-usage';
import { LEGAL_IMPRINT_TEMPLATE_EN } from '../../constants/legal-texts-en/imprint';
import { LEGAL_PRIVACY_POLICY_TEMPLATE_EN } from '../../constants/legal-texts-en/privacy-policy';
import { LEGAL_TERMS_OF_USE_TEMPLATE_EN } from '../../constants/legal-texts-en/terms-of-use';
import { LEGAL_PAGE_TITLES_DATA } from './legal-pages.data';
import { LegalNavbarService } from '../../services/navbar';


@Component({
    selector: 'legal-pages',
    templateUrl: './legal-pages.component.html',
    styleUrls: ['./legal-pages.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class LegalPagesComponent implements OnInit, AfterViewInit, OnDestroy {

	//** Decorators */
	@ViewChild('navTabs', { static: false }) readonly navTabs!: ElementRef;

	//** UI Content */
	cookiesContent 		 : ILegalCookieUsageContent;
	privacyContent 		 : string;
	cancellationContent  : string;
	termsOfUseContent 	 : string;
	affiliateContent 	 : string;
	imprintContent 		 : string;

	//** UI Helpers */
	pageTitles 			 : Record<EnumLegalPageRoute, string>;
	selectedLegalPage 	 : EnumLegalPageRoute = EnumLegalPageRoute.CookiesUsage;
	languageSubscription : Subscription;


	//** Enums -------------------------------------- */
	readonly EnumLegalPageRoute: typeof EnumLegalPageRoute = EnumLegalPageRoute;

	constructor(
		@Inject(EnumLegalInjectionToken.Config)
		public  config				 : ILegalPagesConfig,
		private cdr					 : ChangeDetectorRef,
		private router				 : Router,
		private translationService 	 : AddonLanguageTranslationService,
		private legalContentGenerator: LegalContentGenerator,
		private navBarService 		 : LegalNavbarService
	) {}


	ngOnInit(): void {

		//0 - check if config is provided and valid
		if (!Validator.Schema.isValid(this.config, LegalPagesConfigSchema)) throw new Error(`LegalPagesComponent => ngOnInit => FATAL ERROR: Config for legal pages not provided or invalid.`);

		//1 - update the content on page by language
		this.updatePageContentOnLanguageChange();

		//2 - open the legal page by page route
		this.openLegalPageContentByRoute();
	}

	ngAfterViewInit(): void {

		// set the element ref for updating styles dynamically
		this.navBarService.setNavbarElement(this.navTabs);
	}

	ngOnDestroy(): void {
		this.languageSubscription.unsubscribe();
	}

	navChange(legalPage: EnumLegalPageRoute): void {
		this.selectedLegalPage = legalPage;
	}


	/**------------------------------------------------------
	 * Helper functions
	 */
	//** Opens the legal page */
	private openLegalPageContentByRoute(): void {
		this.router.events.pipe(
			map((event: Event) => (event as Scroll).routerEvent as RouterEvent),
			filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd)
		).subscribe((navigationEndEvent: NavigationEnd) => {
			this.selectedLegalPage = navigationEndEvent.url.split('/').pop() as EnumLegalPageRoute || EnumLegalPageRoute.CookiesUsage;
			this.cdr.markForCheck();
		});
	}

	//** Update the content by language */
	private updatePageContentOnLanguageChange(): void {
		//1 - on language change update language relevant content
		this.languageSubscription = this.translationService.getTranslationObservable().subscribe((selectedLanguage: EnumLanguage) => {

			//a. is selected language is valid?
			if (!Util.Enum.isValid(EnumLanguage, selectedLanguage)) throw new Error(`LegalPagesComponent => ngOnInit => FATAL ERROR: selectedLanguage is not valid: ${selectedLanguage}`);

			//b. get page titles by language relevant
			this.pageTitles = LEGAL_PAGE_TITLES_DATA[selectedLanguage]!;

			//c. get content of the page by language if page is not disabled
			if (!this.config.disabledPages.includes(EnumLegalPageRoute.CookiesUsage)) 	    this.cookiesContent  	 = this.getCookieUsageContent(selectedLanguage);
			if (!this.config.disabledPages.includes(EnumLegalPageRoute.PrivacyPolicy)) 	    this.privacyContent  	 = this.getPrivacyPolicyContent(selectedLanguage);
			if (!this.config.disabledPages.includes(EnumLegalPageRoute.CancellationPolicy)) this.cancellationContent = this.getCancellationPolicyContent(selectedLanguage);
			if (!this.config.disabledPages.includes(EnumLegalPageRoute.TermsOfUse)) 		this.termsOfUseContent   = this.getTermsOfUseContent(selectedLanguage);
			if (!this.config.disabledPages.includes(EnumLegalPageRoute.AffiliateTerms)) 	this.affiliateContent 	 = this.getAffiliateTermsContent(selectedLanguage);
			if (!this.config.disabledPages.includes(EnumLegalPageRoute.Imprint)) 			this.imprintContent 	 = this.getImprintContent(selectedLanguage);
			this.cdr.detectChanges();
		});
	}

	//** Cookie Usage */
	private getCookieUsageContent(language: EnumLanguage): ILegalCookieUsageContent {

		if (this.config.pageContent?.[EnumLegalPageRoute.CookiesUsage]) {
			return {
				introContent: this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.CookiesUsage),
				usageContent: '',
				showSettingsDescriptions: false
			};
		}

		//0 - Cookie Usage structure
		const generatedContent: ILegalCookieUsageContent = {
			introContent: '',
			usageContent: ''
		};

		//1 - get content relative to the language
		switch (language) {
			case EnumLanguage.EN:
				generatedContent.introContent = this.legalContentGenerator.generateContent(LEGAL_COOKIES_USAGE_INTRO_EN);
				generatedContent.usageContent = this.legalContentGenerator.generateContent(LEGAL_COOKIES_USAGE_CODE_EN);
				return Util.Basic.deepCopy(generatedContent);

			case EnumLanguage.DE:
				generatedContent.introContent = this.legalContentGenerator.generateContent(LEGAL_COOKIES_USAGE_INTRO_DE);
				generatedContent.usageContent = this.legalContentGenerator.generateContent(LEGAL_COOKIES_USAGE_CODE_DE);
				return Util.Basic.deepCopy(generatedContent);

			default:
				throw new Error(`LegalPagesComponent => getCookieUsageContent => FATAL ERROR: language selected is not valid: ${language}`);
		}
	}

	//** Privacy Policy */
	private getPrivacyPolicyContent(language: EnumLanguage): string {

		// get content relative to the language
		switch (language) {
			case EnumLanguage.EN:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.PrivacyPolicy, LEGAL_PRIVACY_POLICY_TEMPLATE_EN);

			case EnumLanguage.DE:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.PrivacyPolicy, LEGAL_PRIVACY_POLICY_TEMPLATE_DE);

			default:
				throw new Error(`LegalPagesComponent => getPrivacyPolicyContent => FATAL ERROR: language selected is not valid: ${language}`);
		}
	}

	//** Imprint */
	private getImprintContent(language: EnumLanguage): string {

		// get content relative to the language
		switch (language) {
			case EnumLanguage.EN:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.Imprint, LEGAL_IMPRINT_TEMPLATE_EN);

			case EnumLanguage.DE:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.Imprint, LEGAL_IMPRINT_TEMPLATE_DE);

			default:
				throw new Error(`LegalPagesComponent => getImprintContent => FATAL ERROR: language selected is not valid: ${language}`);
		}
	}

	//** Terms of Use */
	private getTermsOfUseContent(language: EnumLanguage): string {

		// get content relative to the language
		switch (language) {
			case EnumLanguage.EN:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.TermsOfUse, LEGAL_TERMS_OF_USE_TEMPLATE_EN);

			case EnumLanguage.DE:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.TermsOfUse, LEGAL_TERMS_OF_USE_TEMPLATE_DE);

			default:
				throw new Error(`LegalPagesComponent => getTermsOfUseContent => FATAL ERROR: language selected is not valid: ${language}`);
		}
	}

	//** Affiliate Terms */
	private getAffiliateTermsContent(language: EnumLanguage): string {

		// get content relative to the language
		switch (language) {
			case EnumLanguage.EN:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.AffiliateTerms, LEGAL_AFFILIATE_TERMS_TEMPLATE_EN);

			case EnumLanguage.DE:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.AffiliateTerms, LEGAL_AFFILIATE_TERMS_TEMPLATE_DE);

			default:
				throw new Error(`LegalPagesComponent => getAffiliateTermsContent => FATAL ERROR: language selected is not valid: ${language}`);
		}
	}

	//** Cancellation Policy */
	private getCancellationPolicyContent(language: EnumLanguage): string {

		// get content relative to the language
		switch (language) {
			case EnumLanguage.EN:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.CancellationPolicy, LEGAL_CANCELLATION_POLICY_TEMPLATE_EN);

			case EnumLanguage.DE:
				return this.legalContentGenerator.generatePageContent(EnumLegalPageRoute.CancellationPolicy, LEGAL_CANCELLATION_POLICY_TEMPLATE_DE);

			default:
				throw new Error(`LegalPagesComponent => getCancellationPolicyContent => FATAL ERROR: language selected is not valid: ${language}`);
		}
	}
}
